import { useState } from "react"
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

import { GoPlus } from "react-icons/go"

import { Pagination } from "./Pagination"
import Modal from "@/components/Modal"
import Form from "./Form"
import { MdSync } from "react-icons/md"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { AxiosError } from "axios"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Loader2 } from "lucide-react"
import toast from "react-hot-toast"
import { Label } from "@/components/ui/label"

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
}

const postData = async (tahun: string) => {
  const response = await api.post("/master/jenis-ukt/sync", { tahun })
  return response.data
}

const getData = async () => {
  const response = await api.get("/master/jenis-ukt/sublayanan1-plaza-sync")
  return response.data
}

export function DataTable<TData, TValue>({
  columns,
  data,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState({})

  const [showInsert, setShowInsert] = useState(false)
  const [showSync, setShowSync] = useState(false)
  const [showSyncKodeLayananUkt, setShowSyncKodeLayananUkt] = useState(false)
  const [form, setForm] = useState({
    tahun: "",
  })

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  })

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: postData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listJenisUkt"] })
      setShowSync(false)
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const {
    mutate: mutateSyncKodeLayananUkt,
    isPending: isPendingSyncKodeLayananUkt,
  } = useMutation({
    mutationFn: getData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listJenisUkt"] })
      setShowSyncKodeLayananUkt(false)
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSyncJenisUkt = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault()
    mutate(form.tahun)
  }

  const handleSyncKodeLayananUkt = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault()
    mutateSyncKodeLayananUkt()
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3 py-4">
        <Input
          placeholder="Filter by nama jenis ukt..."
          value={table.getColumn("nama_jenis_ukt")?.getFilterValue() as string}
          onChange={(event) =>
            table
              .getColumn("nama_jenis_ukt")
              ?.setFilterValue(event.target.value)
          }
          className="max-w-sm"
        />
        <div className="flex gap-x-3">
          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowSync(true)}
          >
            Sinkronisasi
            <MdSync />
          </Button>
          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowSyncKodeLayananUkt(true)}
          >
            Sinkronisasi Kode Layanan UKT
            <MdSync />
          </Button>

          <Button
            className="flex items-center gap-x-3"
            onClick={() => setShowInsert(true)}
          >
            Insert
            <GoPlus />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  )
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="border rounded-xl">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <br />
      <div>
        <Pagination table={table} />
      </div>

      <Modal show={showInsert} onClose={() => setShowInsert(false)}>
        <Form closeModal={() => setShowInsert(false)} method="POST" />
      </Modal>

      <Modal
        show={showSync}
        onClose={() => setShowSync(false)}
        closeable={!isPending}
      >
        <form onSubmit={handleSyncJenisUkt}>
          <FormHeader
            icon={<MdSync />}
            title="Sinkronisasi Data Jenis UKT"
            description={`Apakah anda yakin akan melakukan sinkronisasi data jenis ukt?`}
          />
          <div>
            <Label>Tahun</Label>
            <Input
              placeholder="Ketik tahun (contoh: 2026)..."
              value={form.tahun}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, tahun: event.target.value }))
              }
            />
          </div>
          <div className="mt-5 flex gap-3">
            <Button
              type="button"
              variant={"outline"}
              disabled={isPending}
              onClick={() => setShowSync(false)}
            >
              Batal
            </Button>
            <Button
              variant={"default"}
              className="flex items-center gap-3"
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Sinkron"
              )}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        show={showSyncKodeLayananUkt}
        onClose={() => setShowSyncKodeLayananUkt(false)}
        closeable={!isPendingSyncKodeLayananUkt}
      >
        <form onSubmit={handleSyncKodeLayananUkt}>
          <FormHeader
            icon={<MdSync />}
            title="Sinkronisasi Kode Layanan UKT"
            description={`Apakah anda yakin akan melakukan sinkronisasi Kode Layanan UKT?`}
          />
          <div className="mt-5 flex gap-3">
            <Button
              type="button"
              variant={"outline"}
              disabled={isPendingSyncKodeLayananUkt}
              onClick={() => setShowSync(false)}
            >
              Batal
            </Button>
            <Button
              variant={"default"}
              className="flex items-center gap-3"
              disabled={isPendingSyncKodeLayananUkt}
            >
              {isPendingSyncKodeLayananUkt ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Sinkron"
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
